import { ApiError, api } from "./api";
import type { NotificationKind } from "./notifications-api";

/**
 * Abonnement Web Push : permission navigateur → abonnement auprès du service
 * de push → enregistrement côté API. La clé publique VAPID vient du serveur,
 * elle n'est pas dupliquée dans la configuration du front.
 */

export type NotificationPreferences = {
  /** false = clés VAPID absentes côté serveur : le push ne peut pas fonctionner. */
  pushConfigured: boolean;
  publicKey: string;
  mutedKinds: NotificationKind[];
  /** Nombre d'appareils abonnés pour ce membre. */
  devices: number;
};

export type PushSupport =
  | { state: "ok" }
  /** iOS n'expose le push qu'aux PWA ajoutées à l'écran d'accueil (iOS 16.4+). */
  | { state: "ios-not-installed" }
  | { state: "unsupported" }
  | { state: "denied" };

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return api.get<NotificationPreferences>("/notifications/preferences");
}

export async function setMutedKinds(
  mutedKinds: NotificationKind[],
): Promise<NotificationPreferences> {
  return api.patch<NotificationPreferences>("/notifications/preferences", { mutedKinds });
}

function isIos(): boolean {
  // iPadOS 13+ se présente comme un Mac : on le distingue au tactile.
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** Ce que le navigateur permet ici et maintenant — avant toute demande de permission. */
export function pushSupport(): PushSupport {
  if (typeof window === "undefined") return { state: "unsupported" };
  const hasApi =
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (!hasApi) {
    // Sur iOS, l'API n'existe que dans une PWA installée : le distinguer permet
    // d'expliquer quoi faire au lieu d'afficher « non pris en charge ».
    return isIos() && !window.matchMedia("(display-mode: standalone)").matches
      ? { state: "ios-not-installed" }
      : { state: "unsupported" };
  }
  if (Notification.permission === "denied") return { state: "denied" };
  return { state: "ok" };
}

/**
 * Le service worker prêt, ou null s'il n'est pas enregistré (cas du dev, où
 * ServiceWorkerRegistration s'abstient pour ne pas casser le HMR).
 *
 * ⚠️ Ne JAMAIS attendre `navigator.serviceWorker.ready` sans avoir vérifié
 * qu'un service worker est enregistré : quand il n'y en a aucun, cette promesse
 * ne rejette pas — elle n'aboutit jamais. Un `try/catch` n'y peut rien, et
 * l'appelant reste suspendu pour toujours. C'est ce qui laissait l'écran
 * Profil › Notifications bloqué sur son squelette de chargement en dev.
 *
 * `getRegistration()`, lui, résout immédiatement avec `undefined` s'il n'y a
 * rien : on s'en sert comme garde avant d'attendre l'activation.
 */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (!existing) return null;
    return (await navigator.serviceWorker.ready) ?? existing;
  } catch {
    return null;
  }
}

/**
 * Un service worker est-il actif sur cet appareil ? Le Web Push en dépend
 * entièrement : c'est lui qui reçoit le message et affiche la notification quand
 * l'application est fermée. Sans lui, aucun abonnement n'est possible — d'où
 * l'intérêt de le dire à l'utilisateur au lieu de lui laisser un interrupteur
 * inerte.
 */
export async function hasServiceWorker(): Promise<boolean> {
  return (await registration()) !== null;
}

/** Cet appareil est-il déjà abonné ? */
export async function isSubscribed(): Promise<boolean> {
  const reg = await registration();
  if (!reg) return false;
  return (await reg.pushManager.getSubscription()) !== null;
}

/**
 * La clé publique VAPID est transmise en base64url ; l'API navigateur veut des
 * octets. Le tampon est alloué explicitement pour obtenir un `Uint8Array`
 * adossé à un `ArrayBuffer` — seul type accepté par `applicationServerKey`.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Abonne cet appareil. Renvoie un motif d'échec plutôt qu'une exception pour
 * que l'écran de réglages puisse expliquer précisément ce qui bloque.
 */
export async function subscribeToPush(
  publicKey: string,
): Promise<
  | { ok: true }
  | { ok: false; reason: "no-sw" | "denied" | "failed" }
  // M19 — l'API refuse de reprendre un endpoint qui appartient à un autre compte.
  // Ce motif est distinct de "failed" pour une raison précise : c'est le seul cas
  // où l'utilisateur peut agir (se déconnecter de l'autre compte sur ce poste).
  // Le fondre dans « abonnement impossible » lui laisserait un interrupteur qui
  // échoue sans raison, et donnerait l'air inutilisable à un poste qui ne l'est pas.
  | { ok: false; reason: "taken"; message: string }
> {
  const reg = await registration();
  if (!reg) return { ok: false, reason: "no-sw" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  try {
    // Réutilise l'abonnement existant s'il y en a un (réinstallation, autre
    // onglet) : le navigateur refuse deux abonnements pour un même SW.
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: "failed" };
    }
    await api.post("/notifications/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return { ok: true };
  } catch (e) {
    // Le message vient de l'API : il explique quoi faire, et ne nomme jamais le
    // compte qui détient l'endpoint.
    if (e instanceof ApiError && e.status === 409) {
      return { ok: false, reason: "taken", message: e.message };
    }
    return { ok: false, reason: "failed" };
  }
}

/** Désabonne cet appareil (navigateur + serveur). */
export async function unsubscribeFromPush(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  // Le serveur d'abord : si le désabonnement navigateur échoue, on ne veut pas
  // d'un endpoint mort qui continue de recevoir.
  await api.post("/notifications/push/unsubscribe", { endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

/**
 * Au-delà, on cesse d'attendre le serveur. La requête part quand même : on
 * refuse simplement de retenir quelqu'un devant un écran encore connecté.
 */
const RELEASE_TIMEOUT_MS = 3_000;

/**
 * Rend l'abonnement de cet appareil au moment de la déconnexion (M19).
 *
 * L'ordre est l'INVERSE de `unsubscribeFromPush()`, et c'est le cœur du
 * correctif :
 *
 * - Ici, le désabonnement NAVIGATEUR est la moitié qui compte, parce que c'est
 *   lui qui fait délivrer un endpoint neuf à la personne suivante. Depuis que
 *   l'API refuse de reprendre l'endpoint d'un autre compte, le sauter
 *   condamnerait le collègue qui prend le comptoir à un refus permanent, sans
 *   aucun recours depuis l'interface. Une ligne serveur qui survit, à l'inverse,
 *   se nettoie d'elle-même : le premier push lui vaut un 404/410, et le service
 *   la purge.
 * - Dans le réglage Profil › Notifications, la priorité est l'opposée : celui
 *   qui coupe ses notifications veut qu'elles cessent, donc le serveur d'abord.
 *
 * Deux fonctions plutôt qu'un drapeau : l'ordre EST la décision, et un booléen
 * en argument l'aurait rendue invisible sur les lieux de l'appel.
 */
export async function releasePushOnLogout(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const { endpoint } = sub;

  await sub.unsubscribe().catch(() => {});
  await Promise.race([
    api.post("/notifications/push/unsubscribe", { endpoint }).catch(() => {}),
    new Promise<void>((resolve) => setTimeout(resolve, RELEASE_TIMEOUT_MS)),
  ]);
}
